// Modified Assets.js
import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import AssetsNavBar from './AssetsNavBar';
import TreeNode from './TreeNode';
import { WebSocketProvider } from '../../context/WebSocketContext';
import { UPDATE_TYPES } from '../../utils/constants';
import { UPDATE_TYPE_PROPERTIES } from '../../utils/constants';
import { fetchNodes, createNode } from '../../services/assets';
import AssetList from './AssetList';
import PointList from './PointList';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS } from '../../utils/constants';
import { Check, X, Plus } from 'lucide-react';
import Analysis from './Analysis';


function Assets({ editEnabled }) {
  const { theme } = useTheme();
  const { permissions, currentSiteId, userSites } = useAuth();
  const [assetsCurrentSelection, setAssetsCurrentSelection] = useState(() => {
    if (permissions.includes(PERMISSIONS.ASSETS.VIEW_ASSETS_HIERARCHY)) {
      return 'hierarchy';
    } else if (permissions.includes(PERMISSIONS.ASSETS.VIEW_ASSETS_LIST)) {
      return 'assets'; 
    } else if (permissions.includes(PERMISSIONS.ASSETS.VIEW_POINTS_LIST)) {
      return 'points';
    } else if (permissions.includes(PERMISSIONS.ASSETS.VIEW_POINTS_ANALYSIS)) {
      return 'analysis';
    }
    return '';
  });
  const canViewRealTimeData = userSites.some(site => site.site_id === currentSiteId && site.supports_real_time_data);

  const [enabledUpdates, setEnabledUpdates] = useState(UPDATE_TYPES);
  const [trees, setTrees] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  
  // State for creating a new tree
  const [isCreatingTree, setIsCreatingTree] = useState(false);
  const [newTreeName, setNewTreeName] = useState('');
  const [createTreeError, setCreateTreeError] = useState('');

  useEffect(() => {
    const loadTrees = async () => {
      try {
        const response = await fetchNodes(currentSiteId, null, true);
        setTrees(response);
      } catch (error) {
        console.error('Error fetching trees:', error);
      }
    };

    loadTrees();
  }, [currentSiteId]);

  const handleAssetsNavigate = (selection) => {
    setAssetsCurrentSelection(selection);
  };

  const handleUpdate = () => {
    // TODO: Implement update logic
  };

  const handleNodeChange = (action, nodeId, updatedNode = null) => {
    console.log("node changed", action, nodeId, updatedNode);
    if (action === 'add') {
      setTrees(prevTrees => [...prevTrees, updatedNode]);
    }
    // Handle other actions if necessary
  };

  const handleSelect = (nodeId) => {
    setSelectedNodeId(nodeId);
  };

  const handleCreateTree = async () => {
    if (!newTreeName.trim()) {
      setCreateTreeError('Tree name cannot be empty');
      return;
    }
    try {
      const newTree = await createNode(currentSiteId, newTreeName.trim(), 'Location', trees, null, null, null, null, null, null);
      setTrees(prevTrees => [...prevTrees, newTree]);
      setIsCreatingTree(false);
      setNewTreeName('');
      setCreateTreeError('');
    } catch (error) {
      console.error('Error creating tree:', error);
      setCreateTreeError(error.message || 'Failed to create tree');
    }
  };

  return (
    <WebSocketProvider>
      <div className="h-[calc(100vh-4px)] w-screen overflow-hidden relative">
        <div className="flex h-full mt-16">
          <AssetsNavBar 
            onNavigate={handleAssetsNavigate} 
            currentSelection={assetsCurrentSelection} 
          />
          {assetsCurrentSelection === 'hierarchy' && (
            <div className="flex-1 overflow-hidden">
              <div className="h-full flex flex-col">
                    <div className="w-full xl:max-w-[60%] sticky top-0 z-[5] p-1 flex items-center space-x-2 shrink-0">
                      {canViewRealTimeData && (
                        <div className="flex space-x-2">
                          {Object.keys(enabledUpdates).map(updateType => {
                            const Icon = UPDATE_TYPE_PROPERTIES[updateType].icon;
                            return (
                              <button
                                key={updateType}
                                onClick={() => setEnabledUpdates(prev => ({
                                  ...prev,
                                  [updateType]: !prev[updateType]
                                }))}
                                className={`px-2.5 py-1 text-xs font-medium border backdrop-blur-sm rounded-full transition-all duration-200 flex items-center gap-1 whitespace-nowrap ${
                                  enabledUpdates[updateType] 
                                  ? updateType === 'alarms'
                                    ? 'border-red-500/30 text-red-500 bg-red-500/10'
                                    : updateType === 'faults' 
                                      ? 'border-orange-500/30 text-orange-500 bg-orange-500/10'
                                      : 'border-blue-500/30 text-blue-500 bg-blue-500/10'
                                  : `border-gray-500/30 text-gray-500 hover:bg-gray-500/10`
                                }`}
                              >
                                <Icon size={14} />
                                <span className="hidden md:inline">
                                  {UPDATE_TYPE_PROPERTIES[updateType].label}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {editEnabled && (
                        <div className="relative">
                          <button
                            onClick={() => {
                            setIsCreatingTree(true);
                            setCreateTreeError('');
                          }}
                          className="px-2.5 py-1 text-xs font-medium border backdrop-blur-sm rounded-full transition-all duration-200 flex items-center gap-1 whitespace-nowrap border-green-500/30 text-green-500 bg-green-500/10 hover:bg-green-500/20"
                          title="Create New Tree"
                        >
                          <Plus size={14} />
                          <span className="hidden md:inline">Create Tree</span>
                        </button>
                        {isCreatingTree && (
                          <div className={`absolute left-0 mt-2 p-4 rounded-xl ${theme === 'dark' ? 'bg-gray-800/90' : 'bg-gray-100/90'} backdrop-blur-md border border-indigo-500/20 shadow-lg hover:shadow-xl transition-all duration-300`}>
                            <div className="flex flex-col gap-3">
                              <div className="flex gap-3">
                                <input
                                  type="text"
                                  placeholder="Tree name"
                                  value={newTreeName}
                                  onChange={(e) => setNewTreeName(e.target.value)}
                                  className={`px-3 py-2 rounded-lg w-full ${theme === 'dark' ? 'bg-gray-700/90 text-white placeholder-gray-400' : 'bg-white/90 text-gray-800 placeholder-gray-500'} border border-indigo-500/30 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 outline-none transition-all duration-200`}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleCreateTree();
                                    }
                                  }}
                                />
                              </div>
                              {createTreeError && (
                                <p className="text-red-500">{createTreeError}</p>
                              )}
                              <div className="flex gap-2 w-full">
                                <button 
                                  onClick={() => {
                                    setIsCreatingTree(false);
                                    setNewTreeName('');
                                    setCreateTreeError('');
                                  }}
                                  className={`flex-1 px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-red-600/20 hover:bg-red-600/30' : 'bg-red-500/10 hover:bg-red-500/20'} text-red-400 font-medium transition-all duration-200 hover:scale-[1.02] hover:shadow-md inline-flex items-center justify-center`}
                                >
                                  <X size={18} className="mr-2" />
                                  <span className="whitespace-nowrap">Cancel</span>
                                </button>
                                <button 
                                  onClick={handleCreateTree}
                                  className={`flex-1 px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-green-600/20 hover:bg-green-600/30' : 'bg-green-500/10 hover:bg-green-500/20'} text-green-400 font-medium transition-all duration-200 hover:scale-[1.02] hover:shadow-md inline-flex items-center justify-center`}
                                >
                                  <Check size={18} className="mr-2" />
                                  <span className="whitespace-nowrap">Create</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      )}
                    </div>
                <div className="flex-1 overflow-auto w-full max-w-full xl:max-w-[60%] pb-20 h-full">
                  {trees.map(tree => (
                    <TreeNode 
                      key={tree.id}
                      node={tree}
                      onUpdate={handleUpdate}
                      onNodeChange={handleNodeChange}
                      enabledUpdates={enabledUpdates}
                      editEnabled={editEnabled}
                      parent={null}
                      siblings={trees}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          {assetsCurrentSelection === 'assets' && (
            <AssetList className="flex-1 w-full max-w-full pb-20 h-full"/>
          )}
          {assetsCurrentSelection === 'points' && (
            <PointList className="flex-1 w-full max-w-full pb-20 h-full"/>
          )}
          {assetsCurrentSelection === 'analysis' && (
            <Analysis className="flex-1 w-full max-w-full pb-20 h-full"/>
          )}
        </div>
      </div>
    </WebSocketProvider>
  );
}

export default Assets;